import * as React from 'react'
import { Link } from 'react-router-dom'

const DEFAULT_TEMPLATE =
  'Zapoznałem się i akceptuję {terms} oraz {privacy} serwisu EasyTacho.'

type Props = {
  checked: boolean
  onChange: (checked: boolean) => void
  /**
   * Treść zgody z tokenami {terms} i {privacy}, które są zamieniane na aktywne
   * linki do dokumentów. Domyślnie polska wersja — strony korzystające z i18n
   * podają przetłumaczony szablon.
   */
  template?: string
  termsLabel?: string
  privacyLabel?: string
  id?: string
  className?: string
  style?: React.CSSProperties
}

// Obowiązkowa akceptacja regulaminu przed przejściem do płatności — wymóg
// weryfikacyjny Przelewy24. Linki otwierają się w nowej karcie, żeby nie
// gubić stanu formularza zakupowego.
export function TermsAcceptance({
  checked,
  onChange,
  template = DEFAULT_TEMPLATE,
  termsLabel = 'Regulamin',
  privacyLabel = 'Politykę prywatności',
  id = 'accept-terms',
  className,
  style,
}: Props) {
  const parts = React.useMemo(
    () => renderTemplate(template, termsLabel, privacyLabel),
    [template, termsLabel, privacyLabel],
  )

  return (
    <label htmlFor={id} className={className} style={{ ...labelStyle, ...style }}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        required
        aria-required="true"
        style={checkboxStyle}
      />
      <span>
        {parts}
        <span aria-hidden="true" style={{ color: '#dc2626' }}>
          {' *'}
        </span>
      </span>
    </label>
  )
}

const LINKS: Record<string, string> = {
  terms: '/terms',
  privacy: '/privacy',
}

function renderTemplate(
  template: string,
  termsLabel: string,
  privacyLabel: string,
): React.ReactNode[] {
  const labels: Record<string, string> = { terms: termsLabel, privacy: privacyLabel }
  // Rozbija szablon na tekst i tokeny {terms} / {privacy}.
  return template.split(/(\{terms\}|\{privacy\})/g).map((chunk, i) => {
    const token = chunk.startsWith('{') ? chunk.slice(1, -1) : null
    if (!token || !LINKS[token]) return <React.Fragment key={i}>{chunk}</React.Fragment>
    return (
      <Link
        key={i}
        to={LINKS[token]}
        target="_blank"
        rel="noreferrer"
        style={linkStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {labels[token]}
      </Link>
    )
  })
}

const labelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1.45,
}

const checkboxStyle: React.CSSProperties = {
  marginTop: 3,
  flexShrink: 0,
}

const linkStyle: React.CSSProperties = {
  color: '#2563eb',
  textDecoration: 'underline',
}
